import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Calendar, MapPin, Users, Loader2, IndianRupee, ScrollText, CheckCircle2, Clock } from 'lucide-react';
import { API } from '../../utils/apiConfig';
import { publicMediaUrl, categoryColor, statusLabel, todayIST, formatEventDate, regCountdown } from '../../components/events/eventUtils';
import { EventBannerCarousel } from '../../components/events/EventBannerCarousel';
import { trackPageView } from '../../utils/analytics';

export default function PublicEventLandingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Track the public landing view so UTM/source from the share link is
    // captured (persisted to localStorage) and attributed to the funnel.
    trackPageView(`/e/${id}`);
    axios.get(`${API}/events/landing/${id}`)
      .then((r) => setEv(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const goRegister = () => {
    const target = `/events/${id}`;
    const loggedIn = !!localStorage.getItem('customer_token');
    if (loggedIn) {
      navigate(target);
    } else {
      // Persist the destination in BOTH localStorage and a `next` query param
      // on /login — the query param makes the redirect target deterministic
      // even if storage state is momentarily out of sync, so registration
      // links never intermittently drop the player back at the homepage.
      localStorage.setItem('post_login_redirect', target);
      navigate(`/login?next=${encodeURIComponent(target)}`);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>;
  if (notFound || !ev) return <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-slate-500 gap-2"><Trophy className="w-10 h-10 opacity-40" /> Event not found</div>;

  const open = ['registration_open', 'ongoing', 'published'].includes(ev.status);

  // Active event coupon preview — same discount rules as the register page.
  const activeCoupon = (() => {
    const c = ev.coupon;
    if (!c || !c.code || c.is_active === false) return null;
    const today = todayIST();
    const inWindow = (!c.valid_from || today >= c.valid_from) && (!c.valid_to || today <= c.valid_to);
    return inWindow ? c : null;
  })();

  const catDiscountedFee = (fee) => {
    if (!activeCoupon || !fee) return null;
    const disc = activeCoupon.discount_type === 'flat'
      ? Number(activeCoupon.discount_value) || 0
      : (fee * (Number(activeCoupon.discount_value) || 0)) / 100;
    const capped = Math.min(disc, fee);
    if (capped <= 0) return null;
    return Math.round((fee - capped) * 100) / 100;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-fuchsia-50 pb-40">
      {/* Banner carousel */}
      <div className="relative">
        <EventBannerCarousel banners={ev.banners} fallbackFileId={ev.banner_file_id} fileUrl={publicMediaUrl} heightClass="h-72 sm:h-[28rem]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 p-5 text-white max-w-2xl mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <img src="/logo192.png" onError={(e) => { e.target.style.display = 'none'; }} alt="" className="w-6 h-6 rounded" />
            <span className="text-xs font-semibold tracking-wide uppercase text-violet-200">MAKS Pickleball</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold">{ev.name}</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-4 relative space-y-4">
        <div className="bg-white rounded-2xl shadow-md p-5">
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
            {ev.venue && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-violet-500" /> {ev.venue}</span>}
            {ev.start_date && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-violet-500" />
                {formatEventDate(ev.start_date)}
                {ev.end_date && ev.end_date !== ev.start_date ? ` – ${formatEventDate(ev.end_date)}` : ''}
              </span>
            )}
            {ev.start_time && (
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-violet-500" /> Starts at {ev.start_time}</span>
            )}
            {ev.show_registration_count !== false && (
              <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-violet-500" /> {ev.total_registrations} registered</span>
            )}
          </div>
          {ev.description && <p className="text-sm text-slate-600 mt-3">{ev.description}</p>}
          {!open && <div className="mt-3 text-sm font-semibold text-amber-600">Registration is currently {statusLabel(ev.status).toLowerCase()}.</div>}
          {(() => {
            const cd = regCountdown(ev.registration_end);
            if (!cd || !open) return null;
            return (
              <div className={`mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cd.days <= 3 ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`} data-testid="landing-reg-countdown">
                <Clock className="w-3 h-3" /> {cd.label}
              </div>
            );
          })()}
        </div>

        {(ev.gallery || []).length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {ev.gallery.map((g, i) => <img key={i} src={publicMediaUrl(g.file_id)} alt="g" className="h-28 w-40 object-cover rounded-xl shrink-0" />)}
          </div>
        )}

        {/* Categories */}
        <div>
          <h2 className="font-semibold text-slate-800 mb-2">Categories</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ev.categories.map((c, i) => {
              const col = categoryColor(i);
              const discFee = catDiscountedFee(c.fee);
              return (
                <div key={c.id} className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 ${col.bg} border-transparent`}>
                  <span className={`w-3 h-3 rounded-full shrink-0 ${col.dot}`} />
                  <div className="flex-1">
                    <div className={`font-semibold ${col.text}`}>{c.name}</div>
                    <div className="text-xs text-slate-500">{statusLabel(c.format)} · {c.slots_left !== null ? `${c.slots_left} slots left` : 'open'}</div>
                  </div>
                  {discFee !== null ? (
                    <div className="text-right">
                      <div className="text-xs text-slate-400 line-through flex items-center justify-end" data-testid={`landing-cat-mrp-${c.id}`}><IndianRupee className="w-3 h-3" />{c.fee}</div>
                      <div className="font-bold text-emerald-600 flex items-center justify-end" data-testid={`landing-cat-discounted-${c.id}`}><IndianRupee className="w-3.5 h-3.5" />{discFee}</div>
                    </div>
                  ) : (
                    <div className="font-bold text-slate-800 flex items-center" data-testid={`landing-cat-fee-${c.id}`}><IndianRupee className="w-3.5 h-3.5" />{c.fee}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {ev.rules && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center gap-2 font-semibold text-slate-800 mb-2"><ScrollText className="w-4 h-4 text-violet-500" /> Rules</div>
            <p className="text-sm text-slate-600 whitespace-pre-line">{ev.rules}</p>
          </div>
        )}

        <div className="flex items-start gap-2 text-xs text-slate-500 px-1">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-500" /> Sign in with your mobile to register — it takes less than a minute.
        </div>
      </div>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-violet-100 p-4 shadow-2xl">
        <div className="max-w-2xl mx-auto">
          <button onClick={goRegister} disabled={!open}
            className="w-full h-12 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 text-white font-bold rounded-xl shadow-lg disabled:opacity-50"
            data-testid="landing-register-btn">
            {open ? 'Register Now' : 'Registration Closed'}
          </button>
        </div>
      </div>
    </div>
  );
}
